<script setup lang="ts">
import { ref, watch, computed } from "vue";
import { useRiskStore } from "@/stores/riskData";
import { storeToRefs } from "pinia";
import {
    CheckPromos,
    DoQuote,
} from "@/composables/useQuoteAPIRequests";
import LoaderOverlay from "@/components/Common/LoaderOverlay.vue";

const props = defineProps<{
    productId: string
}>();

const store = useRiskStore();
const { riskData } = storeToRefs(store);

const quoteId = (window as any).quoteId as string;
const agentId = (window as any).agentId as string;
const ownerId = (window as any).modelData ? (window as any).modelData.OwnerId : null;
const action = ((window as any).quoteAction as string) ?? store.payload?.action ?? "";

// Original (shadow) value for the review display.
const originalPromoCode = computed(() => store.originalValue('PromoCode'));

const promoCode = ref("");
const loading = ref(false);
const showError = ref(false);

watch(
    riskData,
    (newRisk) => {
        if (newRisk?.PromoCode) promoCode.value = newRisk.PromoCode;
    },
    { immediate: true },
);

watch(promoCode, async (newCode) => {
    if (newCode && newCode.length > 0) {
        const { promoValid } = await CheckPromos(newCode, props.productId);
        showError.value = !promoValid.isValid;
    } else {
        showError.value = false;
    }
});

const applyCode = async () => {
    if (showError.value || !promoCode.value.trim()) return;
    loading.value = true;

    const mergedRiskData = { ...(riskData.value ?? {}), PromoCode: promoCode.value };
    store.setPayload({ ...(store.payload ?? {}), riskData: mergedRiskData });
    store.commitShadow(mergedRiskData);

    // Applying a promo code is a neutral re-rate (page 0).
    const { doneQuote } = await DoQuote(
        action, props.productId, quoteId, mergedRiskData, agentId, ownerId, 0,
    );
    if (doneQuote) {
        store.setPayload({ ...(store.payload ?? {}), productOutput: doneQuote });
    }

    loading.value = false;
};
</script>

<template>
    <div class="card mB-10 h-100 w-100">
        <div class="card-header bg-transparent">
            <h5 class="card-title mB-0">
                <em class="far fa-badge-percent"></em> Promotional code
            </h5>
        </div>
        <div class="card-body d-flex align-items-center">
            <form autocomplete="off" class="w-100" @submit.prevent="applyCode">
                <div class="row form-group mB-0">
                    <div class="col-sm-12">
                        <div v-if="store.isReviewMode" class="alert alert-info data-original-values">
                            {{ originalPromoCode == null || originalPromoCode === '' ? 'No original data' : `Original value: ${originalPromoCode}` }}
                        </div>
                        <div class="input-group">
                            <input
                                class="form-control"
                                :class="{ 'border-danger': showError }"
                                type="text"
                                name="PromoCode"
                                v-model="promoCode"
                            />
                            <div class="input-group-append">
                                <button
                                    class="btn btn-outline-secondary"
                                    type="button"
                                    @click.prevent="applyCode"
                                    :disabled="showError"
                                >
                                    Apply
                                </button>
                            </div>
                        </div>
                        <span v-if="showError" class="text-danger">
                            This is not a valid promotion code
                        </span>
                    </div>
                </div>
            </form>
        </div>
    </div>

    <LoaderOverlay
        :show="loading"
        :title="'Loading, please wait'"
        :text="'We are processing your request'"
    />
</template>
